import { config } from '@react-spring/web';
import { useMemo } from 'react';
import { getActualSize, getActualWidth, getTransitionConfig, useDimensions } from '../../utils/index.js';
export const sheetPositionSizes = {
    xs: { minWidth: 288, maxWidth: 576, divide: 5, dir: 'right' },
    sm: { minWidth: 360, maxWidth: 720, divide: 4, dir: 'right' },
    md: { minWidth: 480, maxWidth: 960, divide: 3, dir: 'right' },
    lg: { minWidth: 720, maxWidth: 1440, divide: 2, dir: 'right' },
    xl: { minWidth: 960, maxWidth: 1920, divide: 1.5, dir: 'right' },
    permanent: { minWidth: 360, maxWidth: 720, divide: 4, dir: 'left' },
    bottom: { height: 256, dir: 'bottom' }
};
export const sheetDynamicSizes = {
    xs: { min: 288, max: 576, divide: 5 },
    sm: { min: 360, max: 720, divide: 4 },
    md: { min: 480, max: 960, divide: 3 },
    lg: { min: 720, max: 1440, divide: 2 },
    xl: { min: 960, max: 1920, divide: 1.5 }
};
export const getBorderStyle = ({ isSticky, align }) => {
    const defaultBorder = 'twds-border-none twds-border-0 dark:twds-border-solid dark:twds-border-gray-900';
    if (isSticky) {
        switch (align) {
            case 'bottom':
                return `${defaultBorder} dark:twds-border-t-0 dark:twds-border-r dark:twds-border-l dark:twds-border-b`;
            case 'top':
                return `${defaultBorder} dark:twds-border-t dark:twds-border-r dark:twds-border-l dark:twds-border-b-0`;
            case 'left':
                return `${defaultBorder} dark:twds-border-t dark:twds-border-r-0 dark:twds-border-l dark:twds-border-b`;
            default:
                return `${defaultBorder} dark:twds-border-t dark:twds-border-r dark:twds-border-l-0 dark:twds-border-b`;
        }
    }
    switch (align) {
        case 'bottom':
            return `${defaultBorder} dark:twds-border-t dark:twds-border-r-0 dark:twds-border-l-0 dark:twds-border-b-0`;
        case 'top':
            return `${defaultBorder} dark:twds-border-t-0 dark:twds-border-r-0 dark:twds-border-l-0 dark:twds-border-b`;
        case 'left':
            return `${defaultBorder} dark:twds-border-t-0 dark:twds-border-r dark:twds-border-l-0 dark:twds-border-b-0`;
        default:
            return `${defaultBorder} dark:twds-border-t-0 dark:twds-border-r-0 dark:twds-border-l dark:twds-border-b-0`;
    }
};
export const getAlignmentStyle = (align, isStick) => {
    if (isStick) {
        switch (align) {
            case 'bottom':
                return 'twds-bottom-0 twds-right-0';
            case 'top':
                return 'twds-bottom-0 twds-right-0';
            case 'left':
                return 'twds-top-0 twds-right-0';
            case 'right':
                return 'twds-top-0 twds-right-0';
        }
    }
    switch (align) {
        case 'bottom':
            return 'twds-bottom-0 twds-right-0';
        case 'top':
            return 'twds-top-0 twds-right-0';
        case 'left':
            return 'twds-top-0 twds-left-0';
        case 'right':
            return 'twds-top-0 twds-right-0';
        default:
            return 'twds-right-0 twds-top-0';
    }
};
export const getPosition = (align, isSticky, dimensions) => {
    const { width: navWidth, height: navHeight, left: navLeft } = dimensions ?? {};
    if (align === 'permanent' || isSticky) {
        switch (align) {
            case 'bottom':
                return {
                    top: `${navHeight ?? 0}px`,
                    left: '0px',
                    width: `${navWidth ?? 0}px`
                };
            case 'left':
                return {
                    right: `${navWidth ?? 0}px`,
                    height: `${navHeight ?? 0}px`
                };
            case 'top':
                return {
                    bottom: `${navHeight ?? 0}px`,
                    left: '0px',
                    width: `${navWidth ?? 0}px`
                };
            default:
                return {
                    left: `${navWidth ?? 0}px`,
                    height: `${navHeight ?? 0}px`
                };
        }
    }
    if (align === 'top' || align === 'bottom') {
        return { left: `${navLeft ?? 0}px` };
    }
    return null;
};
export const getAniTransitionConfig = (position, size) => {
    let sizeObj;
    if ((position === 'bottom' && !size) ||
        position === 'permanent' ||
        position === 'lg' ||
        position === 'xl' ||
        position === 'md' ||
        position === 'sm' ||
        position === 'xs') {
        sizeObj = sheetPositionSizes[position];
    }
    const { width: screenWidth = 0, height: screenHeight = 0 } = useDimensions();
    let appliedWidth;
    let appliedHeight;
    appliedWidth = useMemo(() => (sizeObj ? getActualWidth(screenWidth, sizeObj) : undefined), [sizeObj, screenWidth]);
    const { width, height } = useMemo(() => getActualSize({
        screenWidth,
        screenHeight,
        sizeObj: sheetDynamicSizes[!size || typeof size === 'number' ? 'sm' : size]
    }), [screenHeight, screenWidth]);
    if (!sizeObj && (position === 'left' || position === 'right')) {
        if (size === 'lg' || size === 'xl' || size === 'md' || size === 'sm' || size === 'xs') {
            appliedWidth = width;
        }
        else if (size && typeof size === 'number') {
            appliedWidth = size;
        }
        else {
            appliedWidth = width;
        }
    }
    if (!sizeObj && (position === 'bottom' || position === 'top')) {
        if (size === 'lg' || size === 'xl' || size === 'md' || size === 'sm' || size === 'xs') {
            appliedHeight = height;
        }
        else if (size && typeof size === 'number') {
            appliedHeight = size;
        }
        else {
            appliedHeight = height;
        }
    }
    return useMemo(() => {
        const size = {};
        if (!sizeObj && appliedHeight) {
            size.height = appliedHeight;
        }
        else if (!sizeObj && appliedWidth) {
            size.width = appliedWidth;
        }
        else if (position === 'bottom') {
            size.height = sheetPositionSizes[position].height;
        }
        else if (sizeObj && appliedWidth) {
            size.width = appliedWidth;
        }
        return getTransitionConfig(size, config.stiff);
    }, [appliedWidth, appliedHeight, config, position]);
};
//# sourceMappingURL=sheet.logic.js.map